import gql from 'graphql-tag'
import { useQuery } from '@apollo/client'
import { DocumentNode } from 'graphql'

import { formatDate } from '../../../../utils/format'

export enum AudienceType {
  COMMUNITIES = 'communities',
  GROUPS = 'groups',
  EVENTS = 'events'
}

type QueryAudienceI = Record<AudienceType, DocumentNode>

interface AudienceItem {
  id: string
  name: string
}

interface AudienceQueryData {
  communities?: AudienceItem[]
  groups?: (AudienceItem & { shortName: string })[]
  events?: (AudienceItem & { startDate: string | null })[]
  communityAudienceGroups?: {
    name: string
    communities: AudienceItem[]
  }[]
}

const communitiesQuery = gql`
  query audienceQueryCommunities {
    communities(types: [crossGroup, group]) {
      id
      name
    }
  }
`

const communitiesWithAudienceGroupsQuery = gql`
  query audienceQueryCommunitiesWithAudienceGroups {
    communities(types: [crossGroup, group]) {
      id
      name
    }
    communityAudienceGroups {
      name
      communities {
        id
        name
      }
    }
  }
`

const queryByAudience: QueryAudienceI = {
  communities: communitiesQuery,
  groups: gql`
    query audienceQueryGroups {
      groups {
        id
        name
        shortName
      }
    }
  `,
  events: gql`
    query audienceQueryEvents {
      events {
        id
        name
        startDate
      }
    }
  `
}

interface AudienceDataI {
  value: string
  label: string
  custom?: {
    isGroup: true
    groupName: string
    communityIds: string[]
  }
}

const formatDataResponse = (
  rawData: AudienceQueryData,
  audience: AudienceType = AudienceType.COMMUNITIES,
  useShortName: boolean = true
): AudienceDataI[] => {
  const baseOptions = (rawData[audience] || []).map(
    (
      item: AudienceItem & { shortName?: string; startDate?: string | null }
    ) => {
      const label =
        useShortName && item.shortName !== undefined
          ? item.shortName
          : item.name
      const labelWithDate =
        audience === AudienceType.EVENTS && item.startDate != null
          ? `${label} ${formatDate(item.startDate)}`
          : label

      return {
        value: item.id,
        label: labelWithDate
      }
    }
  )

  // Add synthetic group options for COMMUNITIES only
  if (
    audience === AudienceType.COMMUNITIES &&
    rawData.communityAudienceGroups
  ) {
    const groupOptions: AudienceDataI[] = rawData.communityAudienceGroups.map(
      group => ({
        value: `audience-group:${group.name}`,
        label: group.name,
        custom: {
          isGroup: true,
          groupName: group.name,
          communityIds: group.communities.map(c => c.id)
        }
      })
    )

    return [...groupOptions, ...baseOptions]
  }

  return baseOptions
}

const useAudienceQuery = (
  audience: AudienceType = AudienceType.COMMUNITIES,
  useShortName: boolean = audience === AudienceType.GROUPS,
  includeAudienceGroups: boolean = false
) => {
  const query =
    audience === AudienceType.COMMUNITIES && includeAudienceGroups
      ? communitiesWithAudienceGroupsQuery
      : queryByAudience[audience]
  const { data: rawData, refetch } = useQuery<AudienceQueryData>(query)
  return {
    data: rawData ? formatDataResponse(rawData, audience, useShortName) : [],
    refetch
  }
}

export default useAudienceQuery
